/**
 * Shared HR/announcement-style attachment rules (must match backend ANNOUNCEMENT_ATTACHMENT_*).
 */

export const MAX_ATTACHMENTS = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export const ALLOWED_ATTACHMENT_MIME = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
]);

const ATTACHMENT_EXT_TO_MIME = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
};

export const ATTACHMENT_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif';

export const effectiveAttachmentMime = (file) => {
  let t = (file.type || '').trim().toLowerCase();
  if (t === 'image/jpg' || t === 'image/pjpeg') t = 'image/jpeg';
  if (t && t !== 'application/octet-stream') return t;
  const name = (file.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot) : '';
  const fromExt = ATTACHMENT_EXT_TO_MIME[ext];
  if (fromExt) return fromExt;
  return t;
};

export const validateAttachmentFiles = (files) => {
  for (const file of files) {
    const mime = effectiveAttachmentMime(file);
    if (!ALLOWED_ATTACHMENT_MIME.has(mime)) {
      return `"${file.name}" is not a supported file type.`;
    }
    if (file.size > MAX_FILE_BYTES) {
      return `"${file.name}" exceeds the 10 MB size limit.`;
    }
  }
  return null;
};

export const formatAttachmentFileSize = (bytes) => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/** Normalize attachment objects from API / Mongo (snake_case or legacy keys). */
export function normalizeHrAttachments(raw) {
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : typeof raw === 'object' ? [raw] : [];
  return list
    .filter(Boolean)
    .map((att, idx) => {
      const fileUrl = att.file_url || att.fileUrl || '';
      const fileName = att.file_name || att.fileName || 'Attachment';
      const id = att.id != null ? String(att.id) : att._id != null ? String(att._id) : `att-${idx}`;
      const size = att.size;
      return { id, file_name: fileName, file_url: fileUrl, size };
    })
    .filter((a) => a.file_url);
}

export function countHrRequestAttachments(leave) {
  const raw = leave?.attachments;
  if (raw == null) return 0;
  const list = Array.isArray(raw) ? raw : typeof raw === 'object' ? [raw] : [];
  return list.filter(Boolean).length;
}
